'use client';

import { useEffect, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import {
    getDeviceToken,
    getDeviceInfo,
    getNonPiiAccount,
    type DeviceTokenResponse,
} from '@/services/account/getDeviceToken';
import type { YMMData } from '@/interfaces/site/SiteYMMData';

export interface UseDeviceYmmOptions {
    student: string;
    tokenType: string;
}

export interface UseDeviceYmmResult {
    ymm: YMMData | null;
    isLoading: boolean;
}

const lookupCache = new Map<string, Promise<DeviceTokenResponse | null>>();

function runLookup(key: string, run: () => Promise<DeviceTokenResponse | null>) {
    const cached = lookupCache.get(key);
    if (cached) {
        return cached;
    }

    const pending = run().catch((error) => {
        lookupCache.delete(key);
        throw error;
    });

    lookupCache.set(key, pending);
    return pending;
}

export function useDeviceYmm({ student, tokenType }: UseDeviceYmmOptions): UseDeviceYmmResult {
    const searchParams = useSearchParams();
    const dtok = searchParams?.get('dtok') ?? null;
    const radioId = searchParams?.get('radioid') ?? null;
    const rawAct = searchParams?.get('act') ?? null;
    const accountNumber = rawAct ? rawAct.replace(/^\*/, '') : null;

    const hasDtokFlow = !!dtok && !!tokenType && !!student;
    const hasRadioFlow = !!radioId;

    const [ymm, setYmm] = useState<YMMData | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(dtok !== null || radioId !== null);

    useEffect(() => {
        if (!hasDtokFlow && !hasRadioFlow) {
            return;
        }

        let cancelled = false;

        async function fetchYmm() {
            try {
                let response: DeviceTokenResponse | null = null;

                if (hasDtokFlow) {
                    response = await runLookup(`${dtok}:${student}:${tokenType}`, () =>
                        getDeviceToken({ student, token: dtok!, tokenType }),
                    );
                } else if (radioId) {
                    response = accountNumber
                        ? await runLookup(`nonpii:${radioId}:${accountNumber}`, () =>
                            getNonPiiAccount({ radioId, accountNumber }),
                        )
                        : await runLookup(`deviceinfo:${radioId}`, () => getDeviceInfo({ radioId }));
                }

                if (cancelled) return;

                if (response?.vehicleYear && response?.vehicleMake && response?.vehicleModel) {
                    setYmm({
                        year: response.vehicleYear,
                        make: response.vehicleMake,
                        model: response.vehicleModel,
                    });
                    (window as BrowserWindow).pushToAdobe({
                        event: 'device-info',
                        deviceInfo: [
                            { year: response.vehicleYear, make: response.vehicleMake, model: response.vehicleModel },
                        ],
                    });
                } else if (response?.last4DigitsOfRadioId) {
                    setYmm({ last4DigitsOfRadioId: response.last4DigitsOfRadioId });
                    (window as BrowserWindow).pushToAdobe({
                        event: 'device-info',
                        last4DigitsOfRadioId: response.last4DigitsOfRadioId,
                    });
                } else if (radioId) {
                    setYmm({ last4DigitsOfRadioId: radioId.slice(-4) });
                }
            } catch {
                // do not show YMM on failure by design
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        }

        fetchYmm();

        return () => {
            cancelled = true;
        };
    }, [dtok, student, tokenType, radioId, accountNumber, hasDtokFlow, hasRadioFlow]);

    return { ymm, isLoading };
}